import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { formatWithSystemLocale } from '../../utils/dateUtils';
import CurrencyInput from "../../components/CurrencyInput";
import FormattedCurrency from "../../components/FormattedCurrency";

import { useNotification } from "../../contexts/NotificationContext";
import { Stethoscope } from "lucide-react";
import { getApiUrl } from "../../config/api";

export default function MedicalHistory() {
  const { id: horseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning } = useNotification();
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({
    issue: "",
    symptoms: "",
    diagnosis: "",
    treatment: "",
    medications: "",
    treatment_date: "",
    notes: "",
    amount: "",
    veterinarian_id: ""
  });

  const [veterinarians, setVeterinarians] = useState([]);

  useEffect(() => {
    fetchRecords();
    fetchVeterinarians();
  }, []);

  // Check for edit parameter in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const editId = urlParams.get('edit');
    if (editId && records.length > 0) {
      const recordToEdit = records.find(rec => rec.id.toString() === editId);
      if (recordToEdit) {
        startEdit(recordToEdit);
        // Clean up URL by removing the edit parameter
        navigate(`/horses/${horseId}/medicalhistory`, { replace: true });
      }
    }
  }, [records, location.search, navigate, horseId]);

  const fetchVeterinarians = async () => {
    try {
      const res = await axios.get(getApiUrl("/api/veterinarians"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setVeterinarians(res.data);
    } catch (error) {
      console.error("Error fetching veterinarians:", error);
    }
  };

  const fetchRecords = async () => {
    try {
      const res = await axios.get(getApiUrl(`/api/horses/${horseId}/medicalhistory`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setRecords(res.data);
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      // Ensure amount defaults to 0 if empty
      const formData = {
        ...form,
        amount: form.amount || "0.00"
      };
      
      await axios.post(getApiUrl(`/api/horses/${horseId}/medicalhistory`), formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setForm({ issue: "", symptoms: "", diagnosis: "", treatment: "", medications: "", treatment_date: "", notes: "", amount: "", veterinarian_id: "" });
      fetchRecords();
      showSuccess("Medical record added successfully!");
    } catch (err) {
      console.error("Submit error:", err);
      showError(err.response?.data?.error || "Failed to add medical record. Please try again.");
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const goBack = () => navigate("/horses");

 const startEdit = (v) => {
  setEditingId(v.id);
  setEditData({
    issue: v.issue || "",
    symptoms: v.symptoms || "",
    diagnosis: v.diagnosis || "",
    treatment: v.treatment || "",
    medications: v.medications || "",
    treatment_date: v.treatment_date ? v.treatment_date.split("T")[0] : "",
    notes: v.notes || "",
    amount: v.amount || "",
    veterinarian_id: v.veterinarian_id || ""});
};
  const saveEdit = async (id) => {
    try {
      // Ensure amount defaults to 0 if empty
      const editFormData = {
        ...editData,
        amount: editData.amount || "0.00"
      };
      
      await axios.put(getApiUrl(`/api/horses/${horseId}/medicalhistory/${id}`), editFormData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setEditingId(null);
      fetchRecords();
      showSuccess("Medical record updated successfully!");
    } catch (err) {
      console.error("Update error:", err);
      showError(err.response?.data?.error || "Failed to update medical record. Please try again.");
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleDelete = async (recordId) => {
    try {
      await axios.delete(getApiUrl(`/api/horses/${horseId}/medicalhistory/${recordId}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      fetchRecords();
      showSuccess("Medical record deleted successfully!");
    } catch (err) {
      console.error("Delete error:", err);
      showError(err.response?.data?.error || "Failed to delete medical record. Please try again.");
    }
  };

  const [showForm, setShowForm] = useState(false);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      
      <button
        onClick={goBack}
        className="mb-4 px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200 text-sm"
      >
  ← Back to Horses
</button>
      <h2 className="text-2xl font-bold mb-6 flex items-center">
        <Stethoscope className="mr-2 text-blue-600" /> Medical
      </h2>

 {!editingId && (
   <div className="bg-white shadow p-6 rounded mb-8">
     <div className="flex items-center justify-between mb-4">
       <h3 className="text-lg font-semibold">Add Medical Record</h3>
       <button
         onClick={() => setShowForm(!showForm)}
         className="text-sm text-blue-600 hover:underline focus:outline-none"
       >
         {showForm ? "Hide" : "Add"}
       </button>
     </div>

     {showForm && (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Issue *</label>
        <input
          name="issue"
          placeholder="e.g., Colic, Lameness, Injury"
          value={form.issue}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Symptoms</label>
        <input
          name="symptoms"
          placeholder="e.g., Lethargy, Loss of appetite, Limping"
          value={form.symptoms}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Diagnosis</label>
        <input
          name="diagnosis"
          placeholder="e.g., Gastric ulcer, Hoof abscess"
          value={form.diagnosis}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Treatment</label>
        <input
          name="treatment"
          placeholder="e.g., Medication, Surgery, Rest"
          value={form.treatment}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Medications</label>
        <input
          name="medications"
          placeholder="e.g., Bute, Antibiotics, Supplements"
          value={form.medications}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Treatment Date *</label>
        <input
          type="date"
          name="treatment_date"
          value={form.treatment_date}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
          required
        />
        <p className="text-xs text-gray-500 mt-1">When treatment was provided</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Veterinarian</label>
        <select
          name="veterinarian_id"
          value={form.veterinarian_id}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        >
          <option value="">Select a veterinarian</option>
          {veterinarians.map((vet) => (
            <option key={vet.id} value={vet.id}>
              {vet.name} {vet.clinic_name ? `- ${vet.clinic_name}` : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">Choose from your saved veterinarians</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
        <CurrencyInput
          name="amount"
          value={form.amount}
          onChange={(val) => setForm({ ...form, amount: val })}
          locale={navigator.language || 'en-US'}
          currency="USD"
          className="border px-3 py-2 rounded w-full"
          placeholder="0.00"
        />
        <p className="text-xs text-gray-500 mt-1">Medical treatment cost</p>
      </div>
      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <input
          name="notes"
          placeholder="Any additional information about the treatment"
          value={form.notes}
          onChange={handleChange}
          className="border px-3 py-2 rounded w-full"
        />
      </div>
    </div>
  )}

       {showForm && (
         <button
           onClick={handleSubmit}
           className="mt-4 bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
         >
           Save Medical
         </button>
       )}
     </div>
   )}


      <div className="bg-white shadow p-6 rounded">
        <h3 className="text-lg font-semibold mb-4">History</h3>
        {records.length === 0 ? (
          <p className="text-gray-500">No records yet.</p>
        ) : (
        <ul className="divide-y divide-gray-200">
          {records.map((rec) => (
            <li key={rec.id} className="py-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
              {editingId === rec.id ? (
                <div className="w-full sm:flex-grow">
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issue *</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="issue"
                      value={editData.issue}
                      onChange={(e) => setEditData({ ...editData, issue: e.target.value })}
                      placeholder="e.g., Colic, Lameness, Respiratory infection"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Symptoms</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="symptoms"
                      value={editData.symptoms}
                      onChange={(e) => setEditData({ ...editData, symptoms: e.target.value })}
                      placeholder="e.g., Lethargy, Loss of appetite, Difficulty breathing"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diagnosis</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="diagnosis"
                      value={editData.diagnosis}
                      onChange={(e) => setEditData({ ...editData, diagnosis: e.target.value })}
                      placeholder="Veterinary diagnosis or condition"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Treatment</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="treatment"
                      value={editData.treatment}
                      onChange={(e) => setEditData({ ...editData, treatment: e.target.value })}
                      placeholder="Treatment provided or prescribed"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medications</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="medications"
                      value={editData.medications}
                      onChange={(e) => setEditData({ ...editData, medications: e.target.value })}
                      placeholder="Medications prescribed or administered"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Treatment Date *</label>
                    <input
                      type="date"
                      className="border px-2 py-1 rounded w-full"
                      name="treatment_date"
                      value={editData.treatment_date}
                      onChange={(e) => setEditData({ ...editData, treatment_date: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Veterinarian</label>
                    <select
                      className="border px-2 py-1 rounded w-full"
                      name="veterinarian_id"
                      value={editData.veterinarian_id}
                      onChange={(e) => setEditData({ ...editData, veterinarian_id: e.target.value })}
                    >
                      <option value="">Select a veterinarian</option>
                      {veterinarians.map((vet) => (
                        <option key={vet.id} value={vet.id}>
                          {vet.name} {vet.clinic_name ? `- ${vet.clinic_name}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <input
                      className="border px-2 py-1 rounded w-full"
                      name="notes"
                      value={editData.notes}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      placeholder="Any additional information about the medical issue"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cost</label>
                    <CurrencyInput
                      name="amount"
                      value={editData.amount}
                      onChange={(val) => setEditData({ ...editData, amount: val })}
                      locale={navigator.language || 'en-US'}
                      currency="USD"
                      className="border px-2 py-1 rounded w-full"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => saveEdit(rec.id)}
                      className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="bg-gray-300 text-black px-4 py-1 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <p className="font-medium">{rec.issue}</p>
                    {rec.symptoms && (
                      <p className="text-sm text-gray-600">
                        Symptoms: {rec.symptoms}
                      </p>
                    )}
                    <p className="text-sm text-gray-600">
                      Diagnosis: {rec.diagnosis} — Treatment: {rec.treatment}
                    </p>
                    <p className="text-sm text-gray-600">
                      Treated on: {formatWithSystemLocale(rec.treatment_date)} {rec.medications && `| Meds: ${rec.medications}`}
                    </p>
                    {rec.veterinarian_name && (
                      <p className="text-sm text-gray-600">
                        🏥 {rec.veterinarian_name} {rec.veterinarian_clinic && `- ${rec.veterinarian_clinic}`}
                      </p>
                    )}
                    {rec.notes && <p className="text-sm text-gray-500 italic">Notes: {rec.notes}</p>}
                    {rec.amount && (
                      <p className="text-sm text-gray-500 italic">
                        Amount: <FormattedCurrency value={rec.amount} />
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => startEdit(rec)}
                      className="text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(rec.id)}
                      className="text-red-600 hover:text-red-800 font-semibold"
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>

        )}
      </div>
    </div>
  );
}
